/**
 * Minimal dialog focus trap: keeps Tab cycling inside a container and
 * returns focus to the previously focused element on deactivation.
 * Shared by CommandPalette and KeymapOverlay (body teleports — the admin
 * layout's own focus order must not bleed into an open overlay).
 */

const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), ' +
    'select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusTrap {
    activate(container: HTMLElement): void;
    deactivate(): void;
    onKeydown(event: KeyboardEvent): void;
}

export function createFocusTrap(): FocusTrap {
    let container: HTMLElement | null = null;
    let restoreTo: HTMLElement | null = null;

    const activate = (element: HTMLElement): void => {
        container = element;
        restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    };

    const deactivate = (): void => {
        container = null;

        if (restoreTo && restoreTo.isConnected) {
            restoreTo.focus();
        }

        restoreTo = null;
    };

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key !== 'Tab' || !container) {
            return;
        }

        const focusables = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));

        if (!focusables.length) {
            event.preventDefault();
            return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const inside = active instanceof HTMLElement && container.contains(active);

        if (event.shiftKey && (active === first || !inside)) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && (active === last || !inside)) {
            event.preventDefault();
            first.focus();
        }
    };

    return { activate, deactivate, onKeydown };
}
